import { CardConfig } from '../components/SettingsMenu';
import { DEFAULT_CARD_CONFIGS } from '../constants/cardConfigs';
import {
  DashboardTab,
  TabState,
  TabManagerState,
  CreateTabOptions,
  TabTemplate,
} from '../types/tabs.types';
import { getFilteredLayouts } from './layoutUtils';
import { PreselectionType, VariableType } from '../types/dashboard.types';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';

const STORAGE_KEYS = {
  CARDS: 'market-aggregates-cards',
  LAYOUT: 'market-aggregates-layout',
  TABS: 'market-aggregates-tabs',
  TAB_STATES: 'market-aggregates-tab-states',
} as const;

export const loadCardConfigs = (): CardConfig[] => {
  const saved = localStorage.getItem(STORAGE_KEYS.CARDS);

  if (saved) {
    try {
      const savedConfigs = JSON.parse(saved) as CardConfig[];

      // Filter out any cards that are no longer in defaults (like ticker)
      const filteredSaved = savedConfigs.filter((config) =>
        DEFAULT_CARD_CONFIGS.some((defaultConfig) => defaultConfig.id === config.id),
      );

      // Merge with defaults to handle new cards
      const merged = DEFAULT_CARD_CONFIGS.map((defaultConfig) => {
        const savedConfig = filteredSaved.find((c) => c.id === defaultConfig.id);
        return savedConfig ? { ...defaultConfig, enabled: savedConfig.enabled } : defaultConfig;
      });
      return merged;
    } catch (e) {
      console.warn('Failed to parse saved card configs:', e);
      return DEFAULT_CARD_CONFIGS;
    }
  }
  return DEFAULT_CARD_CONFIGS;
};

export const saveCardConfigs = (configs: CardConfig[]): void => {
  localStorage.setItem(STORAGE_KEYS.CARDS, JSON.stringify(configs));
};

export const loadSavedLayouts = (): any | null => {
  const saved = localStorage.getItem(STORAGE_KEYS.LAYOUT);

  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.warn('Failed to parse saved layout:', e);
      return null;
    }
  }
  return null;
};

export const saveLayouts = (layouts: any): void => {
  localStorage.setItem(STORAGE_KEYS.LAYOUT, JSON.stringify(layouts));
};

export const removeSavedLayouts = (): void => {
  localStorage.removeItem(STORAGE_KEYS.LAYOUT);
};

// Tab Management Functions

const generateTabId = (): string => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getDefaultTabState = (
  options: {
    cardConfigs?: CardConfig[];
    selectedPreselection?: PreselectionType;
    selectedVariable?: VariableType;
    selectedZone?: MarketZoneId | null;
    selectedZones?: MarketZoneId[];
  } = {},
): TabState => {
  const cardConfigs = options.cardConfigs || DEFAULT_CARD_CONFIGS;
  return {
    cardConfigs,
    layouts: getFilteredLayouts(cardConfigs),
    selectedPreselection: options.selectedPreselection || 'intraday',
    selectedVariable: options.selectedVariable || 'wind',
    selectedZone: options.selectedZone || null,
    selectedZones: options.selectedZones || [],
  };
};

const getTemplateTabState = (template: TabTemplate): TabState => {
  let cardConfigs: CardConfig[];

  switch (template) {
    case 'minimal':
      cardConfigs = DEFAULT_CARD_CONFIGS.map((config) => ({
        ...config,
        enabled: ['map', 'chart'].includes(config.id),
      }));
      break;
    case 'analytics':
      cardConfigs = DEFAULT_CARD_CONFIGS.map((config) => ({
        ...config,
        enabled: ['chart', 'heatmap', 'models'].includes(config.id),
      }));
      break;
    default:
      cardConfigs = DEFAULT_CARD_CONFIGS;
  }

  return getDefaultTabState({ cardConfigs });
};

export const loadTabManagerState = (): TabManagerState => {
  const savedTabs = localStorage.getItem(STORAGE_KEYS.TABS);
  const savedTabStates = localStorage.getItem(STORAGE_KEYS.TAB_STATES);

  // Check if we need to migrate from old single-dashboard format
  if (!savedTabs) {
    return migrateFromSingleDashboard();
  }

  try {
    const tabs: DashboardTab[] = savedTabs ? JSON.parse(savedTabs) : [];
    const tabStates: Record<string, TabState> = savedTabStates ? JSON.parse(savedTabStates) : {};

    // Ensure we have at least one tab
    if (tabs.length === 0) {
      return createDefaultTabManager();
    }

    // Find active tab or default to first
    const activeTabId = tabs.find((tab) => tab.isActive)?.id || tabs[0].id;

    // Ensure the active tab exists in tabStates
    if (!tabStates[activeTabId]) {
      tabStates[activeTabId] = getDefaultTabState();
    }

    return {
      tabs,
      activeTabId,
      tabStates,
    };
  } catch (e) {
    console.warn('Failed to parse saved tab state:', e);
    return createDefaultTabManager();
  }
};

const migrateFromSingleDashboard = (): TabManagerState => {
  // Try to load existing card configs and layouts
  const existingCardConfigs = loadCardConfigs();
  const existingLayouts = loadSavedLayouts();

  const tabId = generateTabId();
  const defaultTab: DashboardTab = {
    id: tabId,
    name: 'Dashboard',
    isActive: true,
    createdAt: Date.now(),
    lastModified: Date.now(),
  };

  const tabState: TabState = {
    cardConfigs: existingCardConfigs,
    layouts: existingLayouts || getFilteredLayouts(existingCardConfigs),
    selectedPreselection: 'intraday',
    selectedVariable: 'wind',
    selectedZone: null,
    selectedZones: [],
  };

  const tabManagerState: TabManagerState = {
    tabs: [defaultTab],
    activeTabId: tabId,
    tabStates: { [tabId]: tabState },
  };

  // Save the migrated state
  saveTabManagerState(tabManagerState);

  return tabManagerState;
};

const createDefaultTabManager = (): TabManagerState => {
  const tabId = generateTabId();
  const defaultTab: DashboardTab = {
    id: tabId,
    name: 'Dashboard',
    isActive: true,
    createdAt: Date.now(),
    lastModified: Date.now(),
  };

  const tabState = getDefaultTabState();

  return {
    tabs: [defaultTab],
    activeTabId: tabId,
    tabStates: { [tabId]: tabState },
  };
};

export const saveTabManagerState = (state: TabManagerState): void => {
  localStorage.setItem(STORAGE_KEYS.TABS, JSON.stringify(state.tabs));
  localStorage.setItem(STORAGE_KEYS.TAB_STATES, JSON.stringify(state.tabStates));
};

export const createNewTab = (
  currentState: TabManagerState,
  options: CreateTabOptions = {},
): TabManagerState => {
  const tabId = generateTabId();
  const timestamp = Date.now();

  let tabState: TabState;
  let tabName: string;

  if (options.copyFromActiveTab && currentState.tabStates[currentState.activeTabId]) {
    // Fallback to copying from active tab
    tabState = {
      ...currentState.tabStates[currentState.activeTabId],
    };
    tabName =
      options.name ||
      `Copy of ${currentState.tabs.find((t) => t.id === currentState.activeTabId)?.name || 'Dashboard'}`;
  } else if (options.templateType) {
    // Use template
    tabState = getTemplateTabState(options.templateType);
    tabName =
      options.name ||
      `New ${options.templateType.charAt(0).toUpperCase() + options.templateType.slice(1)} Dashboard`;
  } else {
    // Create default tab
    tabState = getDefaultTabState();
    tabName = options.name || `Dashboard ${currentState.tabs.length + 1}`;
  }

  const newTab: DashboardTab = {
    id: tabId,
    name: tabName,
    isActive: true,
    createdAt: timestamp,
    lastModified: timestamp,
  };

  // Update all tabs to be inactive
  const updatedTabs = currentState.tabs.map((tab) => ({ ...tab, isActive: false }));
  updatedTabs.push(newTab);

  const newState: TabManagerState = {
    tabs: updatedTabs,
    activeTabId: tabId,
    tabStates: {
      ...currentState.tabStates,
      [tabId]: tabState,
    },
  };

  saveTabManagerState(newState);
  return newState;
};

export const switchToTab = (currentState: TabManagerState, tabId: string): TabManagerState => {
  const updatedTabs = currentState.tabs.map((tab) => ({
    ...tab,
    isActive: tab.id === tabId,
  }));

  const newState: TabManagerState = {
    ...currentState,
    tabs: updatedTabs,
    activeTabId: tabId,
  };

  saveTabManagerState(newState);
  return newState;
};

export const updateTabState = (
  currentState: TabManagerState,
  tabId: string,
  updates: Partial<TabState>,
): TabManagerState => {
  const prevState = currentState.tabStates[tabId];
  const merged: TabState = {
    ...prevState,
    ...updates,
    // Deep-merge contextState to avoid losing fields from other updates
    contextState: {
      ...(prevState?.contextState || {}),
      ...(updates.contextState || {}),
    },
  };

  const updatedTabStates = {
    ...currentState.tabStates,
    [tabId]: merged,
  };

  // Update the tab's lastModified timestamp
  const updatedTabs = currentState.tabs.map((tab) =>
    tab.id === tabId ? { ...tab, lastModified: Date.now() } : tab,
  );

  const newState: TabManagerState = {
    ...currentState,
    tabs: updatedTabs,
    tabStates: updatedTabStates,
  };

  saveTabManagerState(newState);
  return newState;
};

export const deleteTab = (currentState: TabManagerState, tabId: string): TabManagerState | null => {
  // Don't allow deleting the last tab
  if (currentState.tabs.length <= 1) {
    return null;
  }

  const updatedTabs = currentState.tabs.filter((tab) => tab.id !== tabId);
  const updatedTabStates = { ...currentState.tabStates };
  delete updatedTabStates[tabId];

  // If we're deleting the active tab, switch to the first remaining tab
  let newActiveTabId = currentState.activeTabId;
  if (tabId === currentState.activeTabId) {
    newActiveTabId = updatedTabs[0].id;
  }

  // Update active status
  const finalTabs = updatedTabs.map((tab) => ({
    ...tab,
    isActive: tab.id === newActiveTabId,
  }));

  const newState: TabManagerState = {
    tabs: finalTabs,
    activeTabId: newActiveTabId,
    tabStates: updatedTabStates,
  };

  saveTabManagerState(newState);
  return newState;
};

export const renameTab = (
  currentState: TabManagerState,
  tabId: string,
  newName: string,
): TabManagerState => {
  const updatedTabs = currentState.tabs.map((tab) =>
    tab.id === tabId ? { ...tab, name: newName.trim() || tab.name, lastModified: Date.now() } : tab,
  );

  const newState: TabManagerState = {
    ...currentState,
    tabs: updatedTabs,
  };

  saveTabManagerState(newState);
  return newState;
};
